import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { PrivatesAppointmentsApi } from "@/openapi/generated";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";
import { invalidateAndRefetchAppointmentBookings } from "@/features/appointments/utils/appointment-bookings-query";
import { useToast } from "@/components/feedback";

export const useBookingDelete = () => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const toast = useToast();

  const { mutate, isPending } = useMutation({
    mutationFn: async ({
      appointmentId,
      bookingIds,
    }: {
      appointmentId: number;
      bookingIds: number[];
    }) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      
      const service = createAuthenticatedApi(
        PrivatesAppointmentsApi,
        accessToken,
      );
      
      return service.apiAppointmentsIdBookingsDelete({
        id: appointmentId,
        apiUserTasksGetLastSelectedIdParameter: bookingIds,
      });
    },
    onSuccess: async (_data, variables) => {
      await invalidateAndRefetchAppointmentBookings(queryClient, [
        variables.appointmentId,
      ]);
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.Appointments],
      });
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.AppointmentAvailability],
      });
      toast({
        title: "Booking deleted",
        description: "Booking has been deleted successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to delete booking";
      
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, isPending };
};

